////////////////////// Kendo specific helpers /////////////////////////////

export type Category =
  | 'dropdown'
  | 'date'
  | 'button'
  | 'text'
  | 'checkbox'
  | 'unknown';

const BUTTON_TYPES = new Set(['button', 'submit', 'reset']);
const DATE_TYPES = new Set(['date', 'datetime-local', 'month', 'week']);
const CLASS_GROUPS: Array<{ className: string[]; category: Category }> = [
  {
    className: ['k-dropdownlist', 'k-combobox', 'k-select'],
    category: 'dropdown',
  },
  {
    className: ['k-datepicker', 'k-dateinput', 'k-datetimepicker'],
    category: 'date',
  },
  { className: ['k-checkbox', 'k-switch'], category: 'checkbox' },
  { className: ['k-button'], category: 'button' },
  {
    className: [
      'k-timepicker',
      'k-numerictextbox',
      'k-radiobutton',
      'k-radio',
      'k-textbox',
      'k-slider',
    ],
    category: 'text',
  },
];

export const isTextEntryElement = (el: HTMLElement | null): boolean => {
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();

  if (tag === 'textarea') return true;

  if (tag === 'input') {
    const type = (el as HTMLInputElement).type?.toLowerCase() || 'text';
    // Limit to "text" family per request. (Expand if you want: search, email, url, tel, password)
    return ['text'].includes(type);
  }

  // Kendo components often wrap inputs; try to find a nested input/textarea
  // If the host is a wrapper, check nearest actual input/textarea
  const closestTextEntry = el.closest?.('input[type="text"], textarea');
  return !!closestTextEntry;
};
export function toHTMLElement(input: any): HTMLElement | null {
  if (!input) return null;

  const candidates = [
    input,
    input?.element, // KendoReact often exposes .element
    input?.input, // some Kendo inputs use .input
    input?.wrapper, // Kendo/jQuery wrapper
    input?.nativeEvent?.target, // native DOM event target
    input?.currentTarget, // React SyntheticEvent
    input?.target, // fallback
  ];

  for (const c of candidates) {
    if (c instanceof HTMLElement) return c;

    // jQuery-like wrapper
    if (c && typeof c === 'object' && '0' in c && c[0] instanceof HTMLElement) {
      return c[0];
    }
  }

  return null;
}

export function getDataGtmDlFromElement(el: any): string | null {
  const dom = toHTMLElement(el);
  if (!dom) return null;

  if (dom.dataset?.gtmDl) return dom.dataset.gtmDl;

  // Walk ancestors to find data-gtm-dl
  let node: any = dom;
  while (node && (node.nodeType === 1 || node.nodeType === 11)) {
    const val = node.getAttribute?.('data-gtm-dl');
    if (val) return val;
    node = node.parentElement || node.parentNode || null;
  }

  return null;
}

/* -------------------------- Small utilities -------------------------- */

export const ACTION_BY_CATEGORY: Record<Category, string> = {
  dropdown: 'select',
  date: 'select',
  button: 'click',
  checkbox: 'check or uncheck',
  text: 'interact',
  unknown: 'interact',
};

export function pack(category: Category) {
  return { category, action: ACTION_BY_CATEGORY[category] };
}

export function lower(s?: string): string {
  return (s ?? '').toLowerCase();
}

export function categoryFromInputType(typeAttr: string): Category {
  if (typeAttr === 'hidden') return 'unknown';
  if (BUTTON_TYPES.has(typeAttr)) return 'button';
  if (DATE_TYPES.has(typeAttr)) return 'date';
  if (typeAttr === 'checkbox') return 'checkbox';
  return 'text'; // includes unknown/missing types
}

export function categoryFromClass(cls: string): Category | undefined {
  for (const group of CLASS_GROUPS) {
    if (includesAny(cls, group.className)) return group.category;
  }
  return undefined;
}

export function includesAny(haystack: string, className: string[]): boolean {
  for (const n of className) if (haystack.includes(n)) return true;
  return false;
}

export function normalizeDlFields(val: unknown): Record<string, any> {
  if (!val) return {};
  // If already an object (typical when passed as a React prop)
  if (typeof val === 'object') return val as Record<string, any>;
  // If string, try parse JSON
  if (typeof val === 'string') {
    const s = val.trim();
    if (!s) return {};
    try {
      const parsed = JSON.parse(s);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

/* -------------------------- Small utilities - end -------------------------- */
